import type { WebsiteDefinition } from "@baze/schema";
import { imageSrc, safeImageSrc } from "./helpers";

/**
 * Product feed for Meta catalogs (Commerce Manager → data source → scheduled feed URL), built from the
 * site's own products, menu and services. The published site serves it as /catalog.csv; the dashboard
 * uses the same rules to say how many items are ready and why others are left out.
 */
export interface CatalogItem {
  id: string;
  title: string;
  description: string;
  /** Whole rupiah. */
  price: number;
  /** Image src as stored (made absolute by the caller). */
  image: string;
  /** Page path + section anchor, e.g. "/menu#menu". */
  path: string;
  availability: "in stock" | "out of stock";
}

export interface SkippedItem {
  title: string;
  reason: "no-price" | "no-image";
}

/** FNV-1a: a short, stable id from the section and the item name (renaming an item gives a new id). */
function stableId(s: string): string {
  let h = 0x811c9dc5;
  for (let i = 0; i < s.length; i++) {
    h ^= s.charCodeAt(i);
    h = Math.imul(h, 0x01000193) >>> 0;
  }
  return `baze-${h.toString(36)}`;
}

export function catalogFeed(definition: WebsiteDefinition): { items: CatalogItem[]; skipped: SkippedItem[] } {
  const items: CatalogItem[] = [];
  const skipped: SkippedItem[] = [];
  const seen = new Set<string>();
  for (const page of definition.pages) {
    if (page.seo.noindex) continue;
    for (const s of page.sections) {
      if (s.hidden) continue;
      const rows: Array<{ title: string; description?: string; price?: number; image?: { src: string; width?: number }; sold?: boolean }> = [];
      if (s.type === "products") for (const i of s.content.items) rows.push({ title: i.name, description: i.description, price: i.price, image: i.image, sold: i.status === "sold" });
      else if (s.type === "menu") for (const c of s.content.categories) for (const i of c.items) rows.push({ title: i.name, description: i.description, price: i.price, image: i.image });
      else if (s.type === "services") for (const i of s.content.items) rows.push({ title: i.title, description: i.description, price: i.price, image: i.image });
      else continue;
      for (const r of rows) {
        const title = r.title.trim();
        if (!title) continue;
        if (!r.price || r.price <= 0) {
          skipped.push({ title, reason: "no-price" });
          continue;
        }
        const src = r.image && safeImageSrc(r.image.src) ? imageSrc(r.image, 1600) : null;
        if (!src) {
          skipped.push({ title, reason: "no-image" });
          continue;
        }
        let id = stableId(`${s.id}:${title.toLowerCase()}`);
        while (seen.has(id)) id += "x";
        seen.add(id);
        items.push({
          id,
          title: title.slice(0, 150),
          description: (r.description?.trim() || title).slice(0, 5000),
          price: r.price,
          image: src,
          path: `${page.slug}${s.anchor ? `#${s.anchor}` : ""}`,
          availability: r.sold ? "out of stock" : "in stock",
        });
      }
    }
  }
  return { items, skipped };
}

/** Meta's CSV feed format (comma-separated, quoted). */
export function catalogCsv(items: CatalogItem[], origin: string, brand: string): string {
  const cell = (v: string) => `"${v.replace(/"/g, '""').replace(/[\r\n]+/g, " ")}"`;
  const abs = (src: string) => (src.startsWith("/") ? `${origin}${src}` : src);
  const head = ["id", "title", "description", "availability", "condition", "price", "link", "image_link", "brand"];
  const rows = items.map((i) =>
    [i.id, i.title, i.description, i.availability, "new", `${i.price} IDR`, `${origin}${i.path}`, abs(i.image), brand].map(cell).join(","),
  );
  return [head.join(","), ...rows].join("\n") + "\n";
}
